// Единые хелперы для меток времени из API (R1 исследовательского тестирования 15.09).
// Бэкенд отдаёт LocalDateTime как ISO-8601 со смещением UTC («…Z»); строка без смещения —
// легаси/кэш — трактуется как UTC (parseIsoUtc), а не как локальное время браузера: иначе
// событие 5-минутной давности в Europe/Moscow подписывалось «3 ч назад» (U7), а комментарий
// 11:13 по UTC показывался «11:13» при местных 14:13 (U5). Вывод — всегда в зоне пользователя.

import {isSameCalendarDay, parseIsoUtc} from './date';

/** Парсер строки даты/времени из API: без смещения — UTC; пустая/битая → null. */
export const parseApiDate = (value?: string | null): Date | null => parseIsoUtc(value);

const DEFAULT_DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
};

/** Дата в локальной зоне пользователя (`toLocaleDateString('ru-RU')`); битая → fallback. */
export const formatDate = (
    value?: string | null,
    opts?: Intl.DateTimeFormatOptions,
    fallback = '—',
): string => {
    const d = parseApiDate(value);
    return d ? d.toLocaleDateString('ru-RU', opts) : fallback;
};

/** Дата и время в локальной зоне пользователя («дд.мм.гггг, чч:мм» по умолчанию); битая → fallback. */
export const formatDateTime = (
    value?: string | null,
    opts?: Intl.DateTimeFormatOptions,
    fallback = '—',
): string => {
    const d = parseApiDate(value);
    return d ? d.toLocaleString('ru-RU', opts ?? DEFAULT_DATE_TIME_OPTS) : fallback;
};

/**
 * Относительная подпись события (ru): «только что» / «N мин назад» / «N ч назад» / «N дн назад»,
 * старше недели — короткая дата. `now` параметризован для тестов; считается от UTC-момента,
 * поэтому смещение зоны пользователя на результат не влияет.
 */
export const formatRelative = (value?: string | null, now: number = Date.now()): string => {
    const d = parseApiDate(value);
    if (!d) {
        return '';
    }
    const sec = Math.floor((now - d.getTime()) / 1000);
    if (sec < 60) {
        return 'только что';
    }
    const min = Math.floor(sec / 60);
    if (min < 60) {
        return `${min} мин назад`;
    }
    const hr = Math.floor(min / 60);
    if (hr < 24) {
        return `${hr} ч назад`;
    }
    const day = Math.floor(hr / 24);
    if (day < 7) {
        return `${day} дн назад`;
    }
    return d.toLocaleDateString('ru-RU', {day: '2-digit', month: 'short', year: 'numeric'});
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Значение для `<input type="datetime-local">` — в ЛОКАЛЬНОМ времени (`yyyy-MM-ddTHH:mm`).
 * `toISOString().slice(0, 16)` показывал бы UTC, а браузер читает поле как локальное.
 */
export const toDateTimeLocalValue = (d: Date | null): string => {
    if (!d || Number.isNaN(d.getTime())) {
        return '';
    }
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/**
 * Попадает ли метка времени в текущие календарные сутки пользователя. Сутки — локальные:
 * ровно так их считает бэкенд по заголовку `X-Timezone` (правило «одна носка в сутки», QA-8).
 */
export const isToday = (value?: string | null, now: Date = new Date()): boolean => {
    const d = parseApiDate(value);
    return d != null && isSameCalendarDay(d, now);
};

/** Время «чч:мм» в зоне пользователя; битая строка → fallback. */
export const formatTime = (value?: string | null, fallback = ''): string => {
    const d = parseApiDate(value);
    return d ? d.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'}) : fallback;
};
